/**
 * 顶部导航 SIP 拨号入口 — 点击按钮打开**悬浮窗** (非 Drawer, 不遮罩底层页面),
 * 内置 5 个 Tab: 拨号 / 通信录 / 通话记录 / 消息 / 会议
 *
 * 单例职责:
 *   - 注册 SIP UA (登录后自动 start)
 *   - 订阅 sipService.subscribeCallEvents 把每通话写入后端 sip_call_log
 *   - 订阅 socket.io 的 sip:message / sip:conf-invite 派发为 window CustomEvent
 *     供各 Tab 独立监听 (避免 Tab 间 prop drilling)
 */
import {
  ContactsOutlined,
  HistoryOutlined,
  MessageOutlined,
  PhoneFilled,
  PhoneOutlined,
  SettingOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import { Badge, Button, Tooltip } from 'antd';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser } from '../api/auth-provider.js';
import { TOKEN_KEY } from '../api/client.js';
import { connectRealtime } from '../api/realtime.js';
import {
  getUserExtension,
  isSipCallingEnabledForUser,
  setUserCallingAssignment,
  sipConfigStore,
} from '../api/sip-config.js';
import { sipPhoneApi } from '../api/sip-phone-api.js';
import { sipService } from '../api/sip-service.js';
import { tx } from '../i18n/translate.js';
import { Icon } from '../icons/index.js';
import { CallLogTab } from './sip-phone/CallLogTab.js';
import { ConferenceTab } from './sip-phone/ConferenceTab.js';
import { DialTab } from './sip-phone/DialTab.js';
import { DirectoryTab } from './sip-phone/DirectoryTab.js';
import { FloatingPanel } from './sip-phone/FloatingPanel.js';
import { MessagesTab } from './sip-phone/MessagesTab.js';
import { MicPermissionGate } from './sip-phone/MicPermissionGate.js';

/** 顶部导航的拨号面板 */
export function SipPhone({ label }: { label?: React.ReactNode } = {}) {
  const { data: user } = useGetIdentity<AuthUser>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'dial' | 'directory' | 'calllog' | 'messages' | 'conference'
  >('dial');
  const [prefilledTarget, setPrefilledTarget] = useState<string>('');
  const [openConvId, setOpenConvId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const sipState = useSyncExternalStore(
    sipService.subscribe,
    sipService.getState,
    sipService.getState,
  );
  const cfg = useSyncExternalStore(
    sipConfigStore.subscribe,
    sipConfigStore.get,
    sipConfigStore.get,
  );

  // 登录后自动启动 + 配置/分机变化自动重连
  const cfgSignature = JSON.stringify(cfg);
  const extensionSignature = user?.id ? getUserExtension(user.id) : '';
  const sipCallingEnabled = user?.id ? isSipCallingEnabledForUser(user.id) : false;

  const refreshMyCallingAssignment = useCallback(() => {
    if (!user?.id) return;
    sipPhoneApi
      .getMyCallingAssignment()
      .then(async (assignment) => {
        setUserCallingAssignment(user.id, assignment);
        if (assignment.enabled) {
          const nextConfig = await sipPhoneApi.getCallingConfig();
          sipConfigStore.set(nextConfig);
        }
      })
      .catch(() => setUserCallingAssignment(user.id, { enabled: false, extension: null }));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    sipPhoneApi
      .getMyCallingAssignment()
      .then(async (assignment) => {
        if (cancelled) return;
        setUserCallingAssignment(user.id, assignment);
        if (assignment.enabled) {
          const nextConfig = await sipPhoneApi.getCallingConfig();
          if (!cancelled) sipConfigStore.set(nextConfig);
        }
      })
      .catch(() => {
        if (!cancelled) setUserCallingAssignment(user.id, { enabled: false, extension: null });
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    window.addEventListener('sip-calling-assignment-changed', refreshMyCallingAssignment);
    return () =>
      window.removeEventListener('sip-calling-assignment-changed', refreshMyCallingAssignment);
  }, [refreshMyCallingAssignment]);
  useEffect(() => {
    if (!user?.id) return;
    if (!sipCallingEnabled) {
      sipService.stop();
      return;
    }
    sipService.start(user.id);
  }, [user?.id, sipCallingEnabled, cfgSignature, extensionSignature]);

  // 通话结束自动写入 sip_call_log
  useEffect(() => {
    if (!user?.id) return;
    return sipService.subscribeCallEvents((ev) => {
      sipPhoneApi
        .logCall({
          direction: ev.direction,
          localExtension: getUserExtension(user.id),
          remoteNumber: ev.remote,
          startAt: new Date(ev.startAt ?? ev.endedAt).toISOString(),
          endedAt: new Date(ev.endedAt).toISOString(),
          durationSec: ev.durationSec,
          status: ev.status,
          failureReason: ev.failureReason,
        })
        .then(() => setRefreshKey((k) => k + 1))
        .catch(() => {
          /* 静默, 不影响通话本体 */
        });
    });
  }, [user?.id]);

  // 来电时自动弹 drawer + 切到拨号 tab
  useEffect(() => {
    if (sipState.call === 'incoming') {
      setOpen(true);
      setActiveTab('dial');
    }
  }, [sipState.call]);

  // 桥接 socket.io 事件到 window CustomEvent (跨 Tab 解耦)
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!user?.id || !token) return;
    const sock = connectRealtime(token);
    const onMsg = (payload: unknown) => {
      window.dispatchEvent(new CustomEvent('sip:message', { detail: payload }));
    };
    const onConfInvite = (payload: unknown) => {
      window.dispatchEvent(new CustomEvent('sip:conf-invite', { detail: payload }));
    };
    sock.on('sip:message', onMsg);
    sock.on('sip:conf-invite', onConfInvite);
    return () => {
      sock.off('sip:message', onMsg);
      sock.off('sip:conf-invite', onConfInvite);
    };
  }, [user?.id]);

  const handleDial = useCallback((number: string, _name?: string) => {
    setPrefilledTarget(number);
    setActiveTab('dial');
  }, []);

  const handleMessage = useCallback(async (peer: { userId?: string; name: string }) => {
    if (!peer.userId) return;
    try {
      const conv = await sipPhoneApi.startP2p(peer.userId);
      setOpenConvId(conv.id);
      setActiveTab('messages');
      setRefreshKey((k) => k + 1);
    } catch {
      /* ignore */
    }
  }, []);

  const inActiveCall =
    sipState.call === 'calling' ||
    sipState.call === 'ringing' ||
    sipState.call === 'incoming' ||
    sipState.call === 'in-call';

  const dotColor =
    sipState.call === 'in-call'
      ? 'green'
      : sipState.call === 'incoming' || sipState.call === 'ringing' || sipState.call === 'calling'
        ? 'red'
        : undefined;

  if (!sipCallingEnabled) return null;

  return (
    <>
      {/* 麦克风权限自动弹窗 — prompt/denied 时挂载即弹 */}
      {sipCallingEnabled && <MicPermissionGate />}
      <div style={{ display: 'inline-flex', alignItems: 'center', height: '100%', lineHeight: 1 }}>
        <Badge
          className="crest-header-badge"
          dot={!!dotColor}
          color={dotColor}
          offset={[-2, 2]}
        >
          <Tooltip title={tx('header:sipCallCenter')}>
            <Button
              type="text"
              className="crest-header-action"
              icon={
                inActiveCall ? (
                  <PhoneFilled style={{ fontSize: 16, color: '#52c41a' }} />
                ) : (
                  <Icon.Phone size={16} />
                )
              }
              onClick={async () => {
                setOpen(true);
                // 顺手在 user gesture 内尝试一次 getUserMedia — prompt 状态会触发浏览器
                // 原生权限气泡; granted/denied 状态会立刻 resolve/reject, 无副作用.
                try {
                  if (sipCallingEnabled && navigator.mediaDevices?.getUserMedia) {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stream.getTracks().forEach((t) => t.stop());
                  }
                } catch {
                  /* 静默 — MicPermissionGate 会接管 denied 指引 */
                }
              }}
            >
              {label}
            </Button>
          </Tooltip>
        </Badge>
      </div>
      <FloatingPanel
        open={open}
        onClose={() => setOpen(false)}
        title={tx('header:sipCallCenter')}
        width={340}
        height={600}
        bodyPadding={0}
        bodyScroll={false}
        extra={
          <Tooltip title={tx('header:sipSettings')}>
            <Button
              type="text"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => {
                navigate('/app/admin/call-config/settings');
              }}
            />
          </Tooltip>
        }
      >
        <PhoneShell activeTab={activeTab} onTabChange={setActiveTab}>
          {activeTab === 'dial' && (
            <DialTab user={user} sipState={sipState} cfg={cfg} prefilledTarget={prefilledTarget} />
          )}
          {activeTab === 'directory' && (
            <DirectoryTab onDial={handleDial} onMessage={handleMessage} />
          )}
          {activeTab === 'calllog' && <CallLogTab onDial={handleDial} refreshKey={refreshKey} />}
          {activeTab === 'messages' && (
            <MessagesTab
              currentUserId={user?.id}
              forcedOpenConversationId={openConvId}
              refreshKey={refreshKey}
            />
          )}
          {activeTab === 'conference' && <ConferenceTab sipState={sipState} />}
        </PhoneShell>
      </FloatingPanel>
    </>
  );
}

type TabKey = 'dial' | 'directory' | 'calllog' | 'messages' | 'conference';

/** 手机壳 — 中部内容 + 底部 5 图标 tab 栏 */
function PhoneShell(props: {
  activeTab: TabKey;
  onTabChange: (k: TabKey) => void;
  children: React.ReactNode;
}) {
  const items: Array<{ key: TabKey; label: string; icon: React.ReactNode }> = [
    { key: 'dial', label: '拨号', icon: <PhoneOutlined /> },
    { key: 'directory', label: '通信录', icon: <ContactsOutlined /> },
    { key: 'calllog', label: '记录', icon: <HistoryOutlined /> },
    { key: 'messages', label: '消息', icon: <MessageOutlined /> },
    { key: 'conference', label: '会议', icon: <VideoCameraOutlined /> },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* 内容区 — 可滚动 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, minHeight: 0 }}>{props.children}</div>

      {/* 底部 Tab 栏 — iOS-style */}
      <div
        style={{
          flexShrink: 0,
          height: 58,
          borderTop: '1px solid #f0f1f3',
          background: '#fff',
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
        }}
      >
        {items.map((it) => {
          const active = it.key === props.activeTab;
          return (
            <button
              key={it.key}
              type="button"
              onClick={() => props.onTabChange(it.key)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '6px 0 4px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                color: active ? '#1677ff' : '#8c8c8c',
                transition: 'color 0.15s',
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>{it.icon}</span>
              <span style={{ fontSize: 10, lineHeight: 1.2 }}>{it.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
